import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  TableSortLabel,
  FormControlLabel,
  Switch,
  InputAdornment,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import api from "../../services/api";
import { useSystemMessagesContext } from "../../contexts/SystemMessagesContext";
import env from "../../config/env";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Settings as SettingsIcon
} from "@mui/icons-material";
import * as MuiIcons from "@mui/icons-material";
import AcessoRestrito from '../../components/AcessoRestrito';

function getMuiIconByName(nomeIcone) {
  const IconComponent = MuiIcons[nomeIcone];
  return IconComponent ? <IconComponent /> : <MuiIcons.HelpOutline />;
}

export default function ModuloListagem() {
  const { addError, addSuccess } = useSystemMessagesContext();
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({
    st_nome: "",
    st_descricao: "",
  });
  const [orderBy, setOrderBy] = useState("st_nome");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [moduloSelecionado, setModuloSelecionado] = useState(null);
  const [formData, setFormData] = useState({
    st_nome: "",
    st_descricao: "",
    vl_precoMensal: null,
    st_icone: "",
    bo_ativo: true,
  });
  const [dataHoraAtual, setDataHoraAtual] = useState(new Date());

  const carregarModulos = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY,
        'Content-Type': 'application/json'
      };
      
      const res = await api.get('/modulos', { headers });
      
      // Filtra os módulos baseado no perfil do usuário
      let modulosFiltrados = res.data;
      if (usuario.perfil !== "superusuario") {
        modulosFiltrados = res.data.filter(modulo => 
          modulo.st_codHashEmpresa === usuario.st_codHashEmpresa
        );
      }

      setModulos(modulosFiltrados);
      addSuccess("Módulos carregados com sucesso");
    } catch (err) {
      console.error("Erro ao carregar módulos:", err);
      addError("Erro ao carregar módulos");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Removidas as dependências instáveis

  useEffect(() => {
    carregarModulos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executar apenas uma vez na montagem do componente

  // Atualizar data/hora a cada minuto
  useEffect(() => {
    const interval = setInterval(() => {
      setDataHoraAtual(new Date());
    }, 60000); // 60 segundos

    return () => clearInterval(interval);
  }, []);

  const abrirFormulario = (modulo = null) => {
    setModuloSelecionado(modulo);
    setFormData({
      st_nome: modulo?.st_nome || "",
      st_descricao: modulo?.st_descricao || "",
      vl_precoMensal: modulo?.vl_precoMensal || null,
      st_icone: modulo?.st_icone || "",
      bo_ativo: modulo?.bo_ativo || true,
    });
    setOpenForm(true);
  };

  const visualizarModulo = (modulo) => {
    setModuloSelecionado(modulo);
    setOpenView(true);
  };

  const fecharFormulario = () => {
    setOpenForm(false);
    setModuloSelecionado(null);
  };

  const fecharVisualizacao = () => {
    setOpenView(false);
    setModuloSelecionado(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'vl_precoMensal') {
      const valorNumerico = value === '' ? null : parseFloat(value);
      setFormData(prev => ({ ...prev, [name]: valorNumerico }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const salvarModulo = async () => {
    try {
      setLoading(true);
      const dadosFormatados = {
        ...formData,
        vl_precoMensal: formData.vl_precoMensal ? parseFloat(formData.vl_precoMensal) : null
      };

      const headers = {
        'Authorization': `Bearer ${localStorage.getItem("token")}`,
        'x-second-key': env.SECOND_SECRET_KEY,
        'Content-Type': 'application/json'
      };

      if (moduloSelecionado) {
        // Atualização (PUT)
        await api.put(`/modulos/${moduloSelecionado.id_modulo}`, dadosFormatados, { headers });
        addSuccess("Módulo atualizado com sucesso!");
      } else {
        // Criação (POST)
        await api.post('/modulos', dadosFormatados, { headers });
        addSuccess("Módulo criado com sucesso!");
      }

      setOpenForm(false);
      await carregarModulos();
    } catch (err) {
      console.error("Erro ao salvar módulo:", err);
      addError("Erro ao salvar módulo");
    } finally {
      setLoading(false);
    }
  };

  const deletarModulo = async (id_modulo) => {
    if (window.confirm("Tem certeza que deseja excluir este módulo?")) {
      try {
        setLoading(true);
        const headers = {
          'Authorization': `Bearer ${localStorage.getItem("token")}`,
          'x-second-key': env.SECOND_SECRET_KEY,
          'Content-Type': 'application/json'
        };
        
        await api.delete(`/modulos/${id_modulo}`, { headers });
        addSuccess("Módulo excluído com sucesso!");
        await carregarModulos();
      } catch (err) {
        console.error("Erro ao deletar módulo:", err);
        addError("Erro ao deletar módulo");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFiltroChange = (event) => {
    const { name, value } = event.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(0);
  };

  const modulosFiltrados = modulos
    .filter((modulo) => {
      return (
        modulo.st_nome.toLowerCase().includes(filtros.st_nome.toLowerCase()) &&
        modulo.st_descricao.toLowerCase().includes(filtros.st_descricao.toLowerCase())
      );
    })
    .sort((a, b) => {
      const isAsc = order === "asc";
      if (orderBy === "st_nome" || orderBy === "st_descricao") {
        return isAsc
          ? a[orderBy].localeCompare(b[orderBy])
          : b[orderBy].localeCompare(a[orderBy]);
      } else if (orderBy === "vl_precoMensal") {
        return isAsc
          ? parseFloat(a.vl_precoMensal) - parseFloat(b.vl_precoMensal)
          : parseFloat(b.vl_precoMensal) - parseFloat(a.vl_precoMensal);
      }
      return 0;
    });

  const modulosPaginados = modulosFiltrados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatarDataHora = (data) => {
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AcessoRestrito modulo="modulos">
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
      {/* Box interno com margens de 1,5% */}
      <Box sx={{
        margin: '0 1.5% 1.5% 1.5%',  // 1,5% nas laterais e inferior
        height: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          p: { xs: 2, sm: 3 }, 
          pb: 2,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Typography 
            variant="h5" 
            mb={1} 
            sx={{ 
              fontSize: { xs: '1.5rem', sm: '2rem' },
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <SettingsIcon sx={{ 
              fontSize: { xs: '1.5rem', sm: '2rem' },
              color: '#FFC107' // Mesma cor usada no menu lateral
            }} />
            Módulos do Sistema
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button 
              variant="contained" 
              onClick={() => abrirFormulario()}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: 'auto', sm: '200px' },
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? '#ffffff'
                  : theme.palette.primary.main,
                color: (theme) => theme.palette.mode === 'dark'
                  ? '#000000'
                  : theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? '#f5f5f5'
                    : theme.palette.primary.dark,
                }
              }}
            >
              <AddIcon /> Novo Módulo
            </Button>
          </Box>
        </Box>

        {/* Container da Tabela */}
        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'auto',
            height: {
              xs: 'calc(90vh - 140px)',
              sm: 'calc(90vh - 120px)',
              md: 'calc(90vh - 120px)',
              lg: 'calc(90vh - 120px)'
            },
            maxHeight: {
              xs: 'calc(90vh - 140px)',
              sm: 'calc(90vh - 120px)',
              md: 'calc(90vh - 120px)',
              lg: 'calc(90vh - 120px)'
            },
            width: '100%',
            minWidth: { xs: '100%', sm: '600px' },
            maxWidth: '100%',
            boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.06)',
            borderRadius: 2,
            border: 'none'
          }}
        >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1
            }}
          >
                                                             <CircularProgress />
          </Box>
        )}
        
        <Table sx={{ width: '100%', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "st_nome"}
                  direction={orderBy === "st_nome" ? order : "asc"}
                  onClick={() => handleRequestSort("st_nome")}
                >
                  Nome
                </TableSortLabel>
                <TextField
                  name="st_nome"
                  size="small"
                  fullWidth
                  value={filtros.st_nome}
                  onChange={handleFiltroChange}
                  placeholder="Filtrar por nome"
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "st_descricao"}
                  direction={orderBy === "st_descricao" ? order : "asc"}
                  onClick={() => handleRequestSort("st_descricao")}
                >
                  Descrição
                </TableSortLabel>
                <TextField
                  name="st_descricao"
                  size="small"
                  fullWidth
                  value={filtros.st_descricao}
                  onChange={handleFiltroChange}
                  placeholder="Filtrar por descrição"
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "vl_precoMensal"}
                  direction={orderBy === "vl_precoMensal" ? order : "asc"}
                  onClick={() => handleRequestSort("vl_precoMensal")}
                >
                  Preço Mensal
                </TableSortLabel>
              </TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {modulosPaginados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Nenhum módulo encontrado.
                </TableCell>
              </TableRow>
            ) : (
              modulosPaginados.map((m, index) => (
                <TableRow 
                  key={m.id_modulo}
                  sx={{
                    backgroundColor: (theme) => 
                      index % 2 === 0 
                        ? theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.02)' 
                          : 'rgba(0, 0, 0, 0.02)'
                        : 'transparent',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.08)' 
                        : 'rgba(0, 0, 0, 0.08)',
                    }
                  }}
                >
                  <TableCell sx={{ lineHeight: 1.2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getMuiIconByName(m.st_icone)}
                      {m.st_nome}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ lineHeight: 1.2 }}>
                    {m.st_descricao}
                  </TableCell>
                  <TableCell sx={{ lineHeight: 1.2 }}>
                    R$ {parseFloat(m.vl_precoMensal || 0).toFixed(2)}
                  </TableCell>
                  <TableCell sx={{ lineHeight: 1.2 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Visualizar">
                        <IconButton size="small" onClick={() => visualizarModulo(m)}>
                          <VisibilityIcon fontSize="small" className="material-icon-visibility" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => abrirFormulario(m)}>
                          <EditIcon fontSize="small" className="material-icon-edit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => deletarModulo(m.id_modulo)}>
                          <DeleteIcon fontSize="small" className="material-icon-delete" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

        {/* Paginação fora do TableContainer */}
        <TablePagination
          component="div"
          count={modulosFiltrados.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Itens por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            borderTop: 'none',
            width: '100%',
            marginTop: 0
          }}
        />
      </Box> {/* Fechamento do Box interno com margens */}

      {/* Dialog de Formulário */}
      <Dialog
        open={openForm}
        onClose={fecharFormulario}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        disableBackdropClick
        disablePortal
        hideBackdrop={false}
        sx={{
          '& .MuiDialog-backdrop': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
        PaperProps={{
          sx: {
            width: { xs: '95vw', md: '900px' },
            height: { xs: '95vh', md: '550px' },
            maxWidth: { xs: '95vw', md: '900px' },
            maxHeight: { xs: '95vh', md: '550px' },
            margin: { xs: '2.5vh auto', md: 'auto' },
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderBottom: 1,
          borderColor: 'divider',
          padding: 2,
          fontSize: '1.25rem',
          fontWeight: 500,
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          {moduloSelecionado ? "Editar Módulo" : "Novo Módulo"}
        </DialogTitle>
        <DialogContent sx={{ 
          height: 'calc(100% - 140px)',
          overflow: 'auto',
          padding: 2,
          '&.MuiDialogContent-root': {
            paddingTop: 2
          },
          position: 'relative',
          top: 0,
          marginBottom: '60px',
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem'
          },
          '& .MuiInputBase-input': {
            fontSize: '0.875rem'
          },
          '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem'
          }
        }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Nome do Módulo"
              name="st_nome"
              value={formData.st_nome}
              onChange={handleInputChange}
              required
            />

            <TextField
              fullWidth
              label="Descrição"
              name="st_descricao"
              value={formData.st_descricao}
              onChange={handleInputChange}
              multiline
              rows={3}
            />

            <TextField
              fullWidth
              label="Ícone"
              name="st_icone"
              value={formData.st_icone}
              onChange={handleInputChange}
              helperText="Nome do ícone do Material Icons"
            />

            <TextField
              fullWidth
              label="Preço Mensal"
              name="vl_precoMensal"
              type="number"
              value={formData.vl_precoMensal}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                inputProps: { 
                  step: "0.01",
                  min: "0"
                }
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.bo_ativo}
                  onChange={handleInputChange}
                  name="bo_ativo"
                />
              }
              label="Ativo"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 2,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1,
          height: '60px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          '& .MuiButton-root': {
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            fontWeight: 500
          },
          '& .MuiTypography-root': {
            fontSize: '0.875rem'
          }
        }}>
          <Typography variant="body2" color="text.secondary">
            {formatarDataHora(dataHoraAtual)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={fecharFormulario}
            >
              FECHAR
            </Button>
            <Button
              variant="contained"
              onClick={salvarModulo}
              disabled={loading || !formData.st_nome}
            >
              {loading ? <CircularProgress size={24} /> : "SALVAR"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openView}
        onClose={fecharVisualizacao}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        disableBackdropClick
        disablePortal
        hideBackdrop={false}
        sx={{
          '& .MuiDialog-backdrop': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            overflowY: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderBottom: 1,
          borderColor: 'divider',
          padding: 2,
          fontSize: '1.25rem',
          fontWeight: 500,
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          Visualizar Módulo
        </DialogTitle>
        <DialogContent sx={{ 
          height: 'calc(100% - 140px)',
          overflow: 'auto',
          padding: 2,
          '&.MuiDialogContent-root': {
            paddingTop: 2
          },
          position: 'relative',
          top: 0,
          marginBottom: '60px',
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem'
          },
          '& .MuiInputBase-input': {
            fontSize: '0.875rem'
          },
          '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem'
          }
        }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Nome do Módulo"
              value={moduloSelecionado?.st_nome || ""}
              disabled
            />

            <TextField
              fullWidth
              label="Descrição"
              value={moduloSelecionado?.st_descricao || ""}
              disabled
              multiline
              rows={3}
            />

            <TextField
              fullWidth
              label="Ícone"
              value={moduloSelecionado?.st_icone || ""}
              disabled
              helperText="Nome do ícone do Material Icons"
            />

            <TextField
              fullWidth
              label="Preço Mensal"
              value={moduloSelecionado?.vl_precoMensal || ""}
              disabled
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={moduloSelecionado?.bo_ativo || false}
                  disabled
                />
              }
              label="Ativo"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 2,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1,
          height: '60px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          '& .MuiButton-root': {
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            fontWeight: 500
          },
          '& .MuiTypography-root': {
            fontSize: '0.875rem'
          }
        }}>
          <Typography variant="body2" color="text.secondary">
            {formatarDataHora(dataHoraAtual)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="contained"
              onClick={fecharVisualizacao}
            >
              FECHAR
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      </Box>
    </AcessoRestrito>
  );
}
